import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { AxiosPromise } from 'axios';
import { ServerResponse } from 'data/types'
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { showError } from 'shared/toaster'
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as alarmNotificationProfileSelectors from 'data/alarmNotificationProfile/alarmNotificationProfile.selectors';
import * as alarmNotificationProfileActions from 'data/alarmNotificationProfile/alarmNotificationProfile.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as alarmNotificationApi from 'data/alarmNotificationProfile/alarmNotificationProfile.api';
import {
  AdaptedAlarmNotificationProfile, AlarmNotificationProfile, AlarmsDef, AlarmsInst, InterceptAlarm
} from 'data/alarmNotificationProfile/alarmNotificationProfile.types';
import AlarmNotificationProfileForm from 'routes/AlarmNotificationProfile/components/AlarmNotificationProfileForm';
import { FormTemplate } from 'data/template/template.types';
import * as types from 'data/alarmNotificationProfile/alarmNotificationProfile.types';
import { useAlarmNotificationProfileFieldCreateNew } from '../../hook/useAlarmNotificationProfileFieldCreateNew';
import { alarmNotificationProfileTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode, adaptAlarmNotificationProfileData, addIdToAlarmDef
} from '../../utils/alarmNotificationProfileTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/alarmNotificationProfileTemplateDataToSubmit';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.AdaptedAlarmNotificationProfile
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  alarmNotificationProfileTemplateDef: adaptTemplateVisibilityMode(alarmNotificationProfileTemplate, 'edit'),
  selectedAlarmNotificationProfile: alarmNotificationProfileSelectors
    .getSelectedAlarmNotificationProfile(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedAlarmNotificationProfile: alarmNotificationProfileSelectors
    .getSelectedAlarmNotificationProfile(state)[0],
  alarmNotificationProfile: alarmNotificationProfileSelectors.getAlarmNotificationProfile(state),
  alarmsDefList: alarmNotificationProfileSelectors.getAlarmsDefList(state),
});

const mapDispatch = {
  goToAlarmNotificationProfileList: alarmNotificationProfileActions.goToAlarmNotificationProfileList,
  updateAlarmNotificationProfile: alarmNotificationProfileActions.updateAlarmNotificationProfile,
  getAlarmNotificationProfile: alarmNotificationProfileActions.getAlarmNotificationProfile,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const AlarmNotificationProfileEdit = ({
  history,
  selectedAlarmNotificationProfile,
  originalSelectedAlarmNotificationProfile,
  userId,
  userName,
  isAuditEnabled,
  alarmNotificationProfileTemplateDef,
  alarmNotificationProfile,
  alarmsDefList,
  goToAlarmNotificationProfileList,
  updateAlarmNotificationProfile,
  getAlarmNotificationProfile,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [selectedRows, setSelectedRows] = useState<AlarmsDef[] | AlarmsInst[] | InterceptAlarm[]>([]);
  const [currentContentType, setCurrentContentType] = useState<string>('');
  const [formData, setFormData] = useState<Partial<AdaptedAlarmNotificationProfile> | null>(null);
  const [template, setTemplate] = useState(alarmNotificationProfileTemplateDef);
  const [handleFieldCreateNew] = useAlarmNotificationProfileFieldCreateNew(
    (param) => handleFieldUpdate(param),
  );
  const [formGridData, setFormGridData] = useState<any[] | null>([]);

  const { t } = useTranslation();

  const id = selectedAlarmNotificationProfile?.alarmNtfyProfileId;

  useEffect(() => {
    if (id) {
      getAlarmNotificationProfile(String(id));
    }
  }, [
    id,
    getAlarmNotificationProfile,
  ]);

  useEffect(() => {
    // if the data not finish loading, the alarmNotificationProfile will still be the old one and have different id than the new selected one
    if (selectedAlarmNotificationProfile && selectedAlarmNotificationProfile?.alarmNtfyProfileId !== alarmNotificationProfile?.alarmNtfyProfileId) {
      return;
    }

    if (template && alarmNotificationProfile) {
      const clonedTemplate: FormTemplate | undefined = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      setTemplate(clonedTemplate);

      const adaptedAlarmNotificationProfile = adaptAlarmNotificationProfileData(alarmNotificationProfile);
      setFormData(adaptedAlarmNotificationProfile);

      const contentType = adaptedAlarmNotificationProfile.profile_type;
      setCurrentContentType(contentType);
      setupGridBaseOnProfileType(contentType, adaptedAlarmNotificationProfile);
    }
  }, [
    alarmNotificationProfile,
    alarmsDefList
  ]);

  const setupGridBaseOnProfileType = (profileType: string,
    adaptedAlarmNotificationProfile: AdaptedAlarmNotificationProfile) => {
    if (profileType === 'ALARM_DEFINITION' && alarmsDefList?.length) {
      setFormGridData(addIdToAlarmDef(['alarmId'], alarmsDefList));
      setSelectedRows(adaptedAlarmNotificationProfile?.alarmsDef || []);
    } else if (profileType === 'ALARM_INSTANCE') {
      setFormGridData([]);
      setSelectedRows(adaptedAlarmNotificationProfile?.alarmsInst || []);
    } else if (profileType === 'INTERCEPT_ALARM') {
      setFormGridData([]);
      setSelectedRows(adaptedAlarmNotificationProfile?.interceptAlarm || []);
    }
  }

  const getCreateAlarmNotificationProfileActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;
    if (!formRef.current.isValid(true, true)) return null;

    const alarmNotificationProfileData = formRef.current.getValues();
    if (!alarmNotificationProfileData || !alarmNotificationProfileData?.alarmNtfyProfileId) return null;

    const profileType = alarmNotificationProfileData.profile_type;
    const alarmsDefSelected: string = profileType === 'ALARM_DEFINITION' ?
      (selectedRows as AlarmsDef[]).reduce((resultString, alarmDef, index) => {
        return index === 0 ? alarmDef.alarmId : `${resultString},${alarmDef.alarmId}`;
      }, '') : '';
    const alarmsInstSelected: string = profileType === 'ALARM_INSTANCE' ?
      (selectedRows as AlarmsInst[]).reduce((resultString, alarmInst, index) => {
        const valueString = `${alarmInst.alarmid}|${alarmInst.instance}`;
        return index === 0 ? valueString : `${resultString},${valueString}`;
      }, '') : '';
    const interceptAlarmSelected: string = profileType === 'INTERCEPT_ALARM' ?
      (selectedRows as InterceptAlarm[]).reduce((resultString, interceptAlarm, index) => {
        return index === 0 ? interceptAlarm.survIndex : `${resultString},${interceptAlarm.survIndex}`;
      }, '') : '';

    const adaptedAlarmNotificationProfileData: AlarmNotificationProfile = {
      alarmNtfyProfileId: alarmNotificationProfileData.alarmNtfyProfileId,
      name: alarmNotificationProfileData.name,
      profile_type: alarmNotificationProfileData.profile_type,
      email: alarmNotificationProfileData.email.join(','),
      alarm_definition: alarmsDefSelected,
      alarm_instance: alarmsInstSelected,
      survindex: interceptAlarmSelected,
      frequency: alarmNotificationProfileData.frequency,
      last_mailing_time: alarmNotificationProfileData.last_mailing_time,
    }

    const payload = {
      data: adaptedAlarmNotificationProfileData,
      auditDetails: getAuditDetailsToSubmit(
        alarmNotificationProfileData,
        originalSelectedAlarmNotificationProfile,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    if (selectedRows?.length <= 0 || selectedRows?.length > 10) {
      showError(t('alarmNotificationProfile.form.grid.error'));
      return;
    }
    const payload = getCreateAlarmNotificationProfileActionPayload();
    if (payload) {
      updateAlarmNotificationProfile(payload.data.alarmNtfyProfileId, payload, history);
    }
  };

  const handleCancel = () => goToAlarmNotificationProfileList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AdaptedAlarmNotificationProfile);
    const clonedTemplate = cloneDeep(template);

    if (fieldName === 'profile_type') {
      setCurrentContentType(value);
      setupGridBaseOnProfileType(value, clonedFormData);
    }

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const getInterceptAlarmListByParam = (body: any) : AxiosPromise<ServerResponse<InterceptAlarm>> => {
    return alarmNotificationApi.getInterceptAlarmList(body);
  }

  const getAlarmInstListByParam = (body: any): AxiosPromise<ServerResponse<AlarmsInst>> => {
    return alarmNotificationApi.getAlarmsInstList(body);
  }

  const renderForm = () => {
    if (formData && alarmsDefList && template && formGridData) {
      return (
        <AlarmNotificationProfileForm
          ref={formRef}
          data={formData || []}
          template={template}
          isEditing
          onSelectionChanged={handleSelectionChanged}
          onFieldUpdate={handleFieldUpdate}
          onFieldCreateNew={handleFieldCreateNew}
          gridData={formGridData}
          profileType={currentContentType}
          getAlarmIntercept={getInterceptAlarmListByParam}
          getAlarmInstace={getAlarmInstListByParam}
          alarmDefData={addIdToAlarmDef(['alarmId'], alarmsDefList)}
        />
      );
    }
  };

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (alarmNotificationProfile == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="AlarmNotificationProfileEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('alarmNotificationProfile.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(AlarmNotificationProfileEdit));
